import "@phosphor-icons/web/bold/style.css";
import "@phosphor-icons/web/duotone/style.css";
import "@phosphor-icons/web/fill/style.css";
import "@phosphor-icons/web/light/style.css";
import "@phosphor-icons/web/regular/style.css";
import "@phosphor-icons/web/thin/style.css";

import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/dm-sans/300.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/dm-mono/400.css";
import "@fontsource/dm-mono/500.css";

import "./styles/app.css";
import "./styles/card.css";
import "./styles/components.css";
import "./styles/error.css";
import "./styles/index.css";
import "./styles/mainLayout.css";
import "./styles/modal.css";
import "./styles/tokens.css";

import { BaseLoginResponse } from "./response/seguridad/BaseLoginResponse";
import { errorMessageComponent } from "./components/error/ErrorMessageComponent";
import { accountService } from "./services/seguridad/AccountService";
import { LoginComponent } from "./components/index/login/LoginComponent";
import { loaderComponent } from "./components/index/loader/LoaderComponent";
import { AppComponent, AppData } from "./components/index/app/AppComponent";
import { BaseAsideMenuComponent } from "./components/index/app/BaseAsideMenuComponent";
import { BasePageComponent } from "./components/pages/BasePageComponent";
import { LoginPayload } from "./services/api/payload/seguridad/LoginPayload";
import { grpcTransportProvider } from "./services/grpc/GrpcTransport";
import { connectivityService } from "./services/ConnectivityService";
import { secureStorage } from "./services/seguridad/SecureStorageService";
import { HttpError } from "./services/api/error/HttpError";
import { NotFoundError } from "./services/api/error/NotFoundError";
import { UnauthorizedError } from "./services/api/error/UnauthorizedError";
import {
  AccionAccesoPortal,
  fx_irAlPortalNitria,
  fx_renderAccesoPortal,
  PORTAL_NITRIA_BOTON,
  PORTAL_NITRIA_REINTENTAR_MESSAGE,
  PORTAL_NITRIA_RUTA_SSO,
  PORTAL_NITRIA_SIN_CONEXION_MESSAGE,
  PORTAL_NITRIA_SIN_TOKEN_MESSAGE,
} from "./services/seguridad/portalNitria";

/** Entrada por el portal de Nitria (SSO, decidido 2026-09-30). */
export interface PortalNitriaConfig<TLoginResponse extends BaseLoginResponse> {
  /** Login del portal: a donde va la app sin sesión válida. */
  loginUrl: string;
  /** Inicia sesión con el token que trae el portal. Cada app decide el
   *  transporte, igual que `InitData.login`. */
  login: (token: string) => Promise<TLoginResponse>;
}

/** URL base del transporte gRPC-Web (la SPA ya no usa REST). Lo usan el
 *  arranque de la app (`Init.init`) y la página oculta de ingreso con
 *  contraseña (`fx_initLoginInterno`). */
export function fx_initGrpcTransport(): void {
  grpcTransportProvider.initBaseRoute(__API_BASE_ROUTE__);
}

/** Datos iniciales del formulario de login: vacíos, salvo en `--dev`, donde
 *  llegan precargadas las credenciales de desarrollo. */
export function fx_createLoginPayload(): LoginPayload {
  const loginPayload: LoginPayload = {
    email: "",
    id: null,
    password: "",
  };
  // Credenciales inyectadas por build.js (DEV_LOGIN_EMAIL/PASSWORD): solo tienen
  // valor en --dev; en test y prod se compilan como "" y no viajan en el bundle.
  if (__IS_DEBUG__) {
    loginPayload.email = __DEV_LOGIN_EMAIL__;
    loginPayload.password = __DEV_LOGIN_PASSWORD__;
  }
  return loginPayload;
}

export interface InitData<TLoginResponse extends BaseLoginResponse> {
  /** Login por email/password. Cada app decide el transporte (hoy gRPC para
   *  Plataforma, ver `PlataformaInit.ts`) — este bootstrap no lo sabe. */
  login: (email: string, password: string) => Promise<TLoginResponse>;
  /** Valida un token de sesión persistido. Mismo criterio que `login`. */
  checkToken: (token: string) => Promise<TLoginResponse>;
  //
  logoRoute: string;
  brandRoute: string;
  defaultTitle: string;
  //
  getAsideMenuComponentHandler: (
    loginResponse: TLoginResponse,
  ) => BaseAsideMenuComponent;
  getPagesHandler: (loginResponse: TLoginResponse) => (appRoute: string) => BasePageComponent | null;
  onBeforeAppInit?: (loginResponse: TLoginResponse) => Promise<void>;
  onAfterLogin?: (loginResponse: TLoginResponse) => void;
  /** Si viene, la marca entra solo por el portal de Nitria: la app atiende la
   *  ruta del portal, sin sesión válida redirige a su login y nunca muestra su
   *  pantalla de login. El ingreso con contraseña queda en una página oculta
   *  aparte (`fx_initLoginInterno`). Hoy solo lo pasa Nitrofert. */
  portal?: PortalNitriaConfig<TLoginResponse>;
}

export class Init<TLoginResponse extends BaseLoginResponse> {
  private _data: InitData<TLoginResponse>;

  constructor(data: InitData<TLoginResponse>) {
    this._data = data;
  }

  /** Arranca la app: transporte gRPC-Web, pantalla de carga y validación de
   *  la sesión guardada (o login). Las marcas que entran por el portal de
   *  Nitria (`InitData.portal`) siguen su propio camino, sin login. */
  public init() {
    fx_initGrpcTransport();
    //
    loaderComponent.setLogoRoute(this._data.logoRoute);
    loaderComponent.init();
    errorMessageComponent.init();
    connectivityService.init();
    if (this._data.portal) {
      this._initDesdePortal(this._data.portal);
      return;
    }
    accountService.loadAsync(this._data.checkToken, (result: TLoginResponse | null) => {
      if (result) {
        this._initAppComponent(result);
        return;
      }
      loaderComponent.dispose();
      //
      const loginComponent: LoginComponent<TLoginResponse> =
        new LoginComponent<TLoginResponse>(
          this._data.login,
          this._data.logoRoute,
          fx_createLoginPayload(),
          (response: TLoginResponse) => {
            accountService.setLoginResponse(
              response,
              () => {
                loginComponent.dispose();
                loaderComponent.init();
                this._initAppComponent(response);
              },
              (error) => {
                console.warn(error);
                loginComponent.dispose();
                loaderComponent.init();
                this._initAppComponent(response);
              },
            );
          },
        );
      loginComponent.init();
    });
  }

  //

  /** Arranque de una marca que entra solo por el portal de Nitria. En la
   *  ruta del portal inicia sesión con el token que trae; en cualquier otra,
   *  con la sesión guardada. Si el servidor rechaza la sesión con la app
   *  abierta, redirige al login del portal. */
  private _initDesdePortal(portal: PortalNitriaConfig<TLoginResponse>): void {
    grpcTransportProvider.onSesionRechazada(() =>
      fx_irAlPortalNitria(portal.loginUrl),
    );

    const url = new URL(window.location.href);
    if (url.pathname === PORTAL_NITRIA_RUTA_SSO) {
      const token = url.searchParams.get("token");
      // El token del portal no debe quedar en la barra de direcciones ni en el historial.
      window.history.replaceState(null, "", "/");
      this._entrarConTokenPortal(token, portal);
      return;
    }
    this._entrarConSesionGuardada(portal.loginUrl);
  }

  /** Inicia sesión con el token del portal, guarda la sesión (reemplaza la
   *  que hubiera) y abre la app. Si no se puede, muestra el motivo exacto con
   *  un botón al login del portal. */
  private _entrarConTokenPortal(
    token: string | null,
    portal: PortalNitriaConfig<TLoginResponse>,
  ): void {
    const irAlPortal: AccionAccesoPortal = {
      texto: PORTAL_NITRIA_BOTON,
      onClick: () => fx_irAlPortalNitria(portal.loginUrl),
    };
    if (!token) {
      this._mostrarAccesoPortal(PORTAL_NITRIA_SIN_TOKEN_MESSAGE, irAlPortal);
      return;
    }

    portal
      .login(token)
      .then((response) => {
        // setLoginResponse ya descarta el token que el transporte tenía en memoria.
        const abrir = () => this._initAppComponent(response);
        accountService.setLoginResponse(response, abrir, (error) => {
          console.warn(error);
          abrir();
        });
      })
      .catch((error: unknown) => {
        // Rechazo del servidor: su texto es el motivo. Falla de red: hay que pedir un enlace nuevo.
        const mensaje =
          error instanceof HttpError
            ? error.message
            : PORTAL_NITRIA_SIN_CONEXION_MESSAGE;
        this._mostrarAccesoPortal(mensaje, irAlPortal);
      });
  }

  /** Abre la app con la sesión guardada. Sin sesión, o si el servidor la
   *  rechaza (inexistente, vencida o usuario inactivo), redirige al login del
   *  portal. Ante una falla de red o del servidor no redirige: muestra el
   *  error con un botón para reintentar. */
  private _entrarConSesionGuardada(portalLoginUrl: string): void {
    secureStorage
      .get()
      .then((token) => {
        if (!token) {
          fx_irAlPortalNitria(portalLoginUrl);
          return;
        }

        this._data
          .checkToken(token)
          .then((response) => this._initAppComponent(response))
          .catch((error: unknown) => {
            if (
              error instanceof NotFoundError ||
              error instanceof UnauthorizedError
            ) {
              fx_irAlPortalNitria(portalLoginUrl);
              return;
            }
            const mensaje =
              error instanceof HttpError
                ? error.message
                : PORTAL_NITRIA_REINTENTAR_MESSAGE;
            this._mostrarAccesoPortal(mensaje, {
              texto: "Reintentar",
              onClick: () => window.location.reload(),
            });
          });
      })
      .catch(() => fx_irAlPortalNitria(portalLoginUrl));
  }

  /** Cambia la pantalla de carga por la pantalla de acceso (motivo y botón). */
  private _mostrarAccesoPortal(
    mensaje: string,
    accion: AccionAccesoPortal,
  ): void {
    loaderComponent.dispose();
    const contenedor = document.createElement("div");
    document.body.appendChild(contenedor);
    fx_renderAccesoPortal(contenedor, this._data.logoRoute, mensaje, accion);
  }

  private _getAppData(loginResponse: TLoginResponse): AppData {
    const appData: AppData = {
      asideMenuComponentHandler: () =>
        this._data.getAsideMenuComponentHandler(loginResponse),
      brand: this._data.brandRoute,
      onLogout: () => {
        accountService.logoutAsync(
          () => {
            window.location.reload();
          },
          (error) => {
            console.warn(error);
            window.location.reload();
          },
        );
      },
      pages: this._data.getPagesHandler(loginResponse),
      title: this._data.defaultTitle,
      usuario: loginResponse.nombre,
    };
    return appData;
  }

  private async _initAppComponent(loginResponse: TLoginResponse): Promise<void> {
    await this._data.onBeforeAppInit?.(loginResponse);
    loaderComponent.dispose();
    new AppComponent(this._getAppData(loginResponse)).init();
    this._data.onAfterLogin?.(loginResponse);
  }
}
